import React, { useEffect, useState } from "react";
import Header from "../../../components/Header/Header";
import Tabs from "../../../components/Tabs/Tabs";
import mes_tab_data from "../../../assets/mockedData/mes_tab_data";
import { savePathToLocalStorage } from "../../../utils/helpers";
import mesApi from "../../../Api/mes.api";
import { IngredientLot } from "../../../types/mes";
import { ToastContainer, toast } from "react-toastify";

/**
 * Stock Overview - Current inventory of all ingredient lots
 * Shows remaining quantities, expiry warnings, and allows deactivation
 */
const StockOverview = () => {
  savePathToLocalStorage();

  const [lots, setLots] = useState<IngredientLot[]>([]);
  const [loading, setLoading] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const [filterIngredient, setFilterIngredient] = useState("");

  useEffect(() => {
    loadLots();
  }, [showInactive]);

  const loadLots = async () => {
    try {
      setLoading(true);
      // mesApi.request() already unwraps the response envelope to the `data` array.
      const lots = await mesApi.getIngredientLots({
        is_active: showInactive ? undefined : true,
      });
      setLots(lots || []);
    } catch (error: any) {
      toast.error(`Failed to load stock: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDeactivateLot = async (lot_id: number) => {
    if (!window.confirm("Are you sure you want to deactivate this lot?")) {
      return;
    }

    try {
      await mesApi.updateIngredientLot(lot_id, { is_active: false });
      toast.success("Lot deactivated");
      loadLots();
    } catch (error: any) {
      toast.error(`Failed to deactivate lot: ${error.message}`);
    }
  };

  const handleReactivateLot = async (lot_id: number) => {
    try {
      await mesApi.updateIngredientLot(lot_id, { is_active: true });
      toast.success("Lot reactivated");
      loadLots();
    } catch (error: any) {
      toast.error(`Failed to reactivate lot: ${error.message}`);
    }
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("hr-HR");
  };

  const getStockStatus = (lot: IngredientLot) => {
    const remaining = lot.remaining_kg || 0;
    const total = lot.quantity_kg || 1;
    const pct = (remaining / total) * 100;

    if (pct === 0) return { label: "Empty", class: "stock-empty" };
    if (pct < 20) return { label: "Low ⚠", class: "stock-low" };
    if (pct < 50) return { label: "Medium", class: "stock-medium" };
    return { label: "Good", class: "stock-good" };
  };

  const getExpiryWarning = (expiryDate?: string) => {
    if (!expiryDate) return null;
    const now = new Date();
    const expiry = new Date(expiryDate);
    const daysUntilExpiry = Math.ceil((expiry.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

    if (daysUntilExpiry < 0) return { label: "Expired", class: "expiry-expired" };
    if (daysUntilExpiry <= 7) return { label: `${daysUntilExpiry}d ⚠`, class: "expiry-warning" };
    if (daysUntilExpiry <= 30) return { label: `${daysUntilExpiry}d`, class: "expiry-soon" };
    return null;
  };

  const filteredLots = lots.filter((lot) => {
    if (!filterIngredient) return true;
    const name = lot.zboogie_ingredients?.IngredientName?.toLowerCase() || "";
    return name.includes(filterIngredient.toLowerCase());
  });

  // Group by ingredient
  const groupedByIngredient = filteredLots.reduce((acc, lot) => {
    const ingName = lot.zboogie_ingredients?.IngredientName || "Unknown";
    if (!acc[ingName]) acc[ingName] = [];
    acc[ingName].push(lot);
    return acc;
  }, {} as Record<string, IngredientLot[]>);

  return (
    <div className="mes-page">
      <Header title="MES - Skladiste" />
      <div className="main-content-flex">
        <Tabs data={mes_tab_data} />
        <div className="section mes-content">
          <ToastContainer position="top-center" autoClose={2000} />

          <div style={{ marginBottom: "20px" }}>
            <h2 style={{ marginBottom: "15px" }}>Stanje zaliha</h2>
            <div style={{ display: "flex", gap: "15px", alignItems: "center" }}>
              <input
                type="text"
                placeholder="Pretraži po sirovini..."
                value={filterIngredient}
                onChange={(e) => setFilterIngredient(e.target.value)}
                style={{
                  padding: "8px",
                  border: "1px solid #ddd",
                  borderRadius: "4px",
                  width: "300px",
                }}
              />
              <label style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                <input
                  type="checkbox"
                  checked={showInactive}
                  onChange={(e) => setShowInactive(e.target.checked)}
                />
                Prikaži neaktivne
              </label>
            </div>
          </div>

          {loading ? (
            <p style={{ textAlign: "center", padding: "20px" }}>Učitavanje...</p>
          ) : (
            <div>
              {Object.keys(groupedByIngredient).length === 0 ? (
                <div className="mes-section">
                  <p style={{ textAlign: "center", padding: "40px" }}>Nema dostupnih zaliha</p>
                </div>
              ) : (
                Object.entries(groupedByIngredient).map(([ingredientName, ingredientLots]) => {
                  const totalRemaining = ingredientLots.reduce((sum, lot) => sum + (lot.remaining_kg || 0), 0);
                  return (
                    <div key={ingredientName} className="mes-section" style={{ marginBottom: "30px" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "15px" }}>
                        <h3>{ingredientName}</h3>
                        <span style={{ fontSize: "1.2rem", fontWeight: "600", color: "#007bff" }}>
                          {totalRemaining.toFixed(2)} kg
                        </span>
                      </div>
                      <div className="mes-table-wrap">
                        <table className="mes-table">
                        <thead>
                          <tr>
                            <th>Broj lota</th>
                            <th>Dobavljač</th>
                            <th>Primljeno</th>
                            <th style={{ textAlign: "right" }}>Preostalo</th>
                            <th>Status</th>
                            <th>Rok trajanja</th>
                            <th>Akcije</th>
                          </tr>
                        </thead>
                        <tbody>
                          {ingredientLots.map((lot) => {
                            const status = getStockStatus(lot);
                            const expiryWarning = getExpiryWarning(lot.expiry_date);
                            return (
                              <tr key={lot.lot_id} style={{ opacity: !lot.is_active ? 0.5 : 1 }}>
                                <td>
                                  <strong>{lot.lot_number}</strong>
                                  {!lot.is_active && (
                                    <span style={{
                                      marginLeft: "8px",
                                      padding: "2px 6px",
                                      background: "#e0e0e0",
                                      borderRadius: "3px",
                                      fontSize: "0.75rem",
                                    }}>
                                      Neaktivan
                                    </span>
                                  )}
                                </td>
                                <td>{lot.supplier || "-"}</td>
                                <td>{formatDate(lot.delivery_date)}</td>
                                <td style={{ textAlign: "right", fontFamily: "monospace" }}>
                                  {lot.remaining_kg?.toFixed(2)} / {lot.quantity_kg?.toFixed(2)} kg
                                </td>
                                <td>
                                  <span className={status.class} style={{ padding: "4px 12px", borderRadius: "4px", fontSize: "0.85rem", fontWeight: "500", background: status.class === "stock-good" ? "#d4edda" : status.class === "stock-medium" ? "#fff3cd" : status.class === "stock-low" ? "#f8d7da" : "#e0e0e0", color: status.class === "stock-good" ? "#155724" : status.class === "stock-medium" ? "#856404" : status.class === "stock-low" ? "#721c24" : "#424242" }}>{status.label}</span>
                                </td>
                                <td>
                                  {expiryWarning ? (
                                    <span className={expiryWarning.class} style={{ padding: "4px 12px", borderRadius: "4px", fontSize: "0.85rem", fontWeight: "500", background: expiryWarning.class === "expiry-soon" ? "#cfe2ff" : expiryWarning.class === "expiry-warning" ? "#fff3cd" : "#f8d7da", color: expiryWarning.class === "expiry-soon" ? "#084298" : expiryWarning.class === "expiry-warning" ? "#856404" : "#721c24" }}>
                                      {expiryWarning.label}
                                    </span>
                                  ) : (
                                    formatDate(lot.expiry_date)
                                  )}
                                </td>
                                <td>
                                  {lot.is_active ? (
                                    <button
                                      className="btn-sm btn-danger"
                                      onClick={() => handleDeactivateLot(lot.lot_id)}
                                    >
                                      Deaktiviraj
                                    </button>
                                  ) : (
                                    <button
                                      className="btn-sm btn-secondary"
                                      onClick={() => handleReactivateLot(lot.lot_id)}
                                    >
                                      Reaktiviraj
                                    </button>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

export default StockOverview;
